import type { BaseSensorEntry } from 'types'

/** URL scheme the tag-linked markdown renderer intercepts. Fragment-first so
 * react-markdown's default url sanitizer leaves it alone. */
export const TAG_LINK_PREFIX = '#tag:'

export interface TagMentionMatch {
  start: number
  end: number
  id: string
}

/**
 * Every spelling of a tag a reply might use, keyed lowercase, back to the
 * tag's id: the full id, the id without its site prefix, and the tag's name.
 * A spelling shared by two different tags is ambiguous and dropped, so a
 * mention never links to the wrong pen.
 */
export const buildTagMentionIndex = (
  entries: readonly BaseSensorEntry[]
): ReadonlyMap<string, string> => {
  const index = new Map<string, string>()
  const ambiguous = new Set<string>()
  for (const entry of entries) {
    const spellings = new Set(
      [entry.id, entry.id.replace(/^[^-]*-/, ''), entry.data?.name]
        .filter((s): s is string => typeof s === 'string' && s.length >= 3)
        .map((s) => s.trim().toLowerCase())
    )
    for (const key of spellings) {
      const existing = index.get(key)
      if (existing && existing !== entry.id) ambiguous.add(key)
      else if (!existing) index.set(key, entry.id)
    }
  }
  for (const key of ambiguous) index.delete(key)
  return index
}

/** A run of characters that can spell a tag id: no spaces, no punctuation
 * beyond the separators tag ids use, and never starting or ending on one. */
const TOKEN = /[A-Za-z0-9][A-Za-z0-9_.\-:/]*[A-Za-z0-9]|[A-Za-z0-9]/g

/** Non-overlapping tag mentions in free text, matched on whole tokens. */
export const findTagMentions = (
  text: string,
  index: ReadonlyMap<string, string>
): TagMentionMatch[] => {
  const matches: TagMentionMatch[] = []
  if (index.size === 0) return matches
  for (const hit of text.matchAll(TOKEN)) {
    const id = index.get(hit[0].toLowerCase())
    if (id)
      matches.push({ start: hit.index, end: hit.index + hit[0].length, id })
  }
  return matches
}

interface MdNode {
  type: string
  value?: string
  url?: string
  title?: string | null
  children?: MdNode[]
}

const SKIP_TYPES = new Set([
  'code',
  'link',
  'linkReference',
  'image',
  'imageReference',
  'html',
  'definition',
])

const link = (id: string, child: MdNode): MdNode => ({
  type: 'link',
  url: `${TAG_LINK_PREFIX}${encodeURIComponent(id)}`,
  title: id,
  children: [child],
})

const linkifyText = (node: MdNode, index: ReadonlyMap<string, string>) => {
  const text = node.value ?? ''
  const matches = findTagMentions(text, index)
  if (!matches.length) return [node]
  const out: MdNode[] = []
  let cursor = 0
  for (const match of matches) {
    if (match.start > cursor)
      out.push({ type: 'text', value: text.slice(cursor, match.start) })
    out.push(
      link(match.id, {
        type: 'text',
        value: text.slice(match.start, match.end),
      })
    )
    cursor = match.end
  }
  if (cursor < text.length)
    out.push({ type: 'text', value: text.slice(cursor) })
  return out
}

const walk = (node: MdNode, index: ReadonlyMap<string, string>) => {
  if (SKIP_TYPES.has(node.type) || !node.children) return
  node.children = node.children.flatMap((child) => {
    if (child.type === 'text') return linkifyText(child, index)
    // Replies put tag ids in backticks; a code span that IS a tag id links too,
    // keeping its code styling inside the link.
    if (child.type === 'inlineCode') {
      const id = index.get((child.value ?? '').trim().toLowerCase())
      return id ? [link(id, child)] : [child]
    }
    walk(child, index)
    return [child]
  })
}

/** Remark plugin: tag mentions in prose and in code spans become `#tag:` links. */
export function remarkTagLinks(options: {
  index: ReadonlyMap<string, string>
}) {
  return (tree: MdNode): void => {
    walk(tree, options.index)
  }
}
